import { ActivityIcon, LayersIcon, ListChecksIcon, UsersRoundIcon } from "lucide-react";
import { useSearch } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useI18n } from "~/i18n";
import { MorrowWorkspacePage } from "../workspace/MorrowWorkspacePage";
import { SelectItem } from "../ui/select";
import { ActivityFilterAllItem, ActivityFilterBox } from "./ActivitiesFilterBar";
import { mockActivitiesFeed } from "./activitiesMock";
import { buildActivitiesFeed } from "./fetchActivitiesFeed";
import { isActivitiesMockMode } from "./activitiesMockMode";
import type {
  ActivitiesFeed,
  ActivityEvent,
  ActivitiesFilters,
  ActivityKind,
  ActivityStatus,
} from "./activitiesTypes";
import type { ActivityCardActionId } from "./activityCardActions";
import { ActivityDetailSheet } from "./ActivityDetailSheet";
import { useActivityCardAction } from "./useActivityCardAction";
import { ActivitiesPromptBar } from "./ActivitiesPromptBar";
import { ActivityLaneSection, activityLaneStackPadBottom } from "./ActivityLaneSection";
import { ActivitiesQuickAccess } from "./ActivitiesQuickAccess";
import { ActivityTimeline } from "./ActivityTimeline";
import { ActivitiesDayStripCell } from "./ActivitiesDayStripCell";
import {
  activityKindLabelKey,
  activityStatusLabelKey,
  FILTER_ICON_CLASS,
} from "./activityPresentation";
import {
  dayStripAround,
  eventsForDay,
  filterActivities,
  groupByLane,
  localDayKey,
} from "./groupActivitiesByDay";

const EMPTY_ACTIVITIES_FEED: ActivitiesFeed = { members: [], events: [], summaryByDay: {} };

export function ActivitiesPage() {
  const { t, locale } = useI18n();
  const search = useSearch({ from: "/activities" });
  const mockMode = isActivitiesMockMode(search);

  const [selectedDay, setSelectedDay] = useState(() =>
    typeof search.day === "string" && /^\d{4}-\d{2}-\d{2}$/.test(search.day)
      ? search.day
      : localDayKey(new Date()),
  );
  const [filters, setFilters] = useState<ActivitiesFilters>({
    memberUserId: null,
    status: "all",
    kind: "all",
  });
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selectedDayRef = useRef<HTMLButtonElement>(null);

  const { mergeEvents, handleAction, isBusy } = useActivityCardAction({
    mockMode,
    onReview: setSelectedId,
  });

  // Real feed: fetch + poll live activity (agent/workflow runs, approvals, agent-to-agent comms).
  // Mock mode keeps the hand-built sample feed for design/dev.
  const [liveFeed, setLiveFeed] = useState<ActivitiesFeed>(EMPTY_ACTIVITIES_FEED);
  useEffect(() => {
    if (mockMode) return;
    let alive = true;
    const load = () =>
      void buildActivitiesFeed()
        .then((f) => {
          if (alive) setLiveFeed(f);
        })
        .catch(() => {});
    load();
    const timer = setInterval(load, 20000);
    return () => {
      alive = false;
      clearInterval(timer);
    };
  }, [mockMode]);
  const feed = useMemo(() => (mockMode ? mockActivitiesFeed() : liveFeed), [mockMode, liveFeed]);
  const events = useMemo(() => mergeEvents(feed.events), [feed.events, mergeEvents]);
  const dayStrip = useMemo(() => dayStripAround(parseDayKey(selectedDay)), [selectedDay]);
  const todayKey = useMemo(() => localDayKey(new Date()), []);

  useEffect(() => {
    selectedDayRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
      inline: "center",
    });
  }, [selectedDay, dayStrip]);

  const dayEvents = useMemo(() => {
    const forDay = eventsForDay(events, selectedDay);
    return filterActivities(forDay, filters);
  }, [events, selectedDay, filters]);

  const lanes = useMemo(() => groupByLane(dayEvents), [dayEvents]);

  const laneRowStackPad = useMemo(() => {
    return Math.max(
      activityLaneStackPadBottom(lanes.todo.length),
      activityLaneStackPadBottom(lanes.doing.length),
      activityLaneStackPadBottom(lanes.done.length),
    );
  }, [lanes]);

  const selectedEvent: ActivityEvent | null = events.find((e) => e.id === selectedId) ?? null;

  const onCardAction = useCallback(
    (actionId: ActivityCardActionId, event: ActivityEvent) => {
      void handleAction(actionId, event);
    },
    [handleAction],
  );

  const headerActions = (
    <div className="flex w-full min-w-0 flex-col gap-2 sm:flex-row sm:items-center sm:justify-end sm:gap-2">
      <ActivitiesPromptBar className="w-full min-w-0 flex-1 sm:max-w-md lg:max-w-lg" />
      <div className="flex shrink-0 items-center justify-end gap-0.5">
        <MemberFilter
          members={feed.members}
          value={filters.memberUserId}
          onChange={(memberUserId) => setFilters((f) => ({ ...f, memberUserId }))}
        />
        <StatusFilter
          value={filters.status}
          onChange={(status) => setFilters((f) => ({ ...f, status }))}
        />
        <KindFilter value={filters.kind} onChange={(kind) => setFilters((f) => ({ ...f, kind }))} />
      </div>
    </div>
  );

  return (
    <MorrowWorkspacePage
      title={t("activities.title")}
      subtitle={t("activities.subtitle")}
      icon={<ActivityIcon />}
      wide
      action={headerActions}
      compactHeader
      contentClassName="gap-6 px-0 sm:px-0"
    >
      <div className="w-full overflow-x-auto overscroll-x-contain scroll-smooth px-4 [scrollbar-width:thin] snap-x snap-mandatory sm:px-6">
        <div className="flex w-max gap-2 pb-0.5">
          {dayStrip.map((dayKey) => {
            const summary = feed.summaryByDay[dayKey];
            const total = summary ? summary.todo + summary.doing + summary.done : 0;
            const active = dayKey === selectedDay;
            return (
              <ActivitiesDayStripCell
                key={dayKey}
                ref={active ? selectedDayRef : undefined}
                dayKey={dayKey}
                locale={locale}
                todayKey={todayKey}
                active={active}
                hasEvents={total > 0}
                needsAttention={summary ? summary.todo > 0 : false}
                onSelect={() => setSelectedDay(dayKey)}
              />
            );
          })}
        </div>
      </div>

      <div className="grid gap-4 px-4 sm:px-6 lg:grid-cols-3 lg:items-start">
        {(["todo", "doing", "done"] as const).map((lane) => (
          <ActivityLaneSection
            key={lane}
            lane={lane}
            events={lanes[lane]}
            rowStackPadBottom={laneRowStackPad}
            selectedId={selectedId}
            onSelect={setSelectedId}
            onAction={onCardAction}
            isActionBusy={isBusy}
          />
        ))}
      </div>

      <div className="grid gap-4 px-4 sm:px-6 lg:grid-cols-[minmax(0,1fr)_min(22rem,34%)] lg:items-start">
        <section className="flex min-w-0 flex-col gap-2.5">
          <div>
            <h2 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {t("activities.timeline")}
            </h2>
            <p className="mt-0.5 text-xs text-muted-foreground/90">
              {t("activities.timeline.hint")}
            </p>
          </div>
          <ActivityTimeline events={dayEvents} selectedId={selectedId} onSelect={setSelectedId} />
        </section>
        <aside className="flex min-w-0 flex-col gap-2.5 lg:sticky lg:top-4 lg:self-start">
          <div>
            <h2 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {t("activities.quickAccess.title")}
            </h2>
            <p className="mt-0.5 text-xs text-muted-foreground/90">
              {t("activities.quickAccess.hint")}
            </p>
          </div>
          <ActivitiesQuickAccess
            events={dayEvents}
            mockMode={mockMode}
            selectedId={selectedId}
            onSelect={setSelectedId}
          />
        </aside>
      </div>

      <ActivityDetailSheet
        event={selectedEvent}
        mockMode={mockMode}
        open={selectedEvent !== null}
        onOpenChange={(open) => {
          if (!open) setSelectedId(null);
        }}
      />
    </MorrowWorkspacePage>
  );
}

function parseDayKey(key: string): Date {
  const [y, m, d] = key.split("-").map(Number);
  return new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1);
}

function MemberFilter({
  members,
  value,
  onChange,
}: {
  readonly members: readonly { userId: string; name: string }[];
  readonly value: string | null;
  readonly onChange: (id: string | null) => void;
}) {
  const memberName = value ? (members.find((m) => m.userId === value)?.name ?? null) : null;

  return (
    <ActivityFilterBox
      variant="minimal"
      icon={<UsersRoundIcon className={FILTER_ICON_CLASS} strokeWidth={1.75} aria-hidden />}
      labelKey="activities.filter.member"
      value={value ?? "all"}
      active={value !== null}
      valueLabel={memberName}
      onValueChange={(v) => onChange(v === "all" ? null : v)}
    >
      <ActivityFilterAllItem labelKey="activities.filter.allMembers" />
      {members.map((m) => (
        <SelectItem key={m.userId} value={m.userId}>
          {m.name}
        </SelectItem>
      ))}
    </ActivityFilterBox>
  );
}

function StatusFilter({
  value,
  onChange,
}: {
  readonly value: ActivityStatus | "all";
  readonly onChange: (v: ActivityStatus | "all") => void;
}) {
  const { t } = useI18n();
  const options: Array<ActivityStatus | "all"> = [
    "all",
    "needs_approval",
    "running",
    "completed",
    "failed",
  ];
  const valueLabel = value === "all" ? null : t(activityStatusLabelKey(value));

  return (
    <ActivityFilterBox
      variant="minimal"
      icon={<ListChecksIcon className={FILTER_ICON_CLASS} strokeWidth={1.75} aria-hidden />}
      labelKey="activities.filter.status"
      value={value}
      active={value !== "all"}
      valueLabel={valueLabel}
      onValueChange={(v) => onChange(v as ActivityStatus | "all")}
    >
      {options.map((opt) => (
        <SelectItem key={opt} value={opt}>
          {opt === "all" ? t("activities.filter.allStatuses") : t(activityStatusLabelKey(opt))}
        </SelectItem>
      ))}
    </ActivityFilterBox>
  );
}

function KindFilter({
  value,
  onChange,
}: {
  readonly value: ActivityKind | "all";
  readonly onChange: (v: ActivityKind | "all") => void;
}) {
  const { t } = useI18n();
  const options: Array<ActivityKind | "all"> = [
    "all",
    "tool_action",
    "workflow_run",
    "agent_run",
    "allowance",
    "schedule",
    "comms",
  ];
  const valueLabel = value === "all" ? null : t(activityKindLabelKey(value));

  return (
    <ActivityFilterBox
      variant="minimal"
      icon={<LayersIcon className={FILTER_ICON_CLASS} strokeWidth={1.75} aria-hidden />}
      labelKey="activities.filter.kind"
      value={value}
      active={value !== "all"}
      valueLabel={valueLabel}
      onValueChange={(v) => onChange(v as ActivityKind | "all")}
    >
      {options.map((opt) => (
        <SelectItem key={opt} value={opt}>
          {opt === "all" ? t("activities.filter.allKinds") : t(activityKindLabelKey(opt))}
        </SelectItem>
      ))}
    </ActivityFilterBox>
  );
}
